import type { PlanEdge, PlanNode } from '../types';
import type { XY } from './geometry';

export const COLUMN_GAP = 120;
export const ROW_GAP = 40;
/** Between the accepted layers and the row of proposals under them. */
export const PROPOSED_GAP = 80;
export const FALLBACK_SIZE = { width: 172, height: 48 };

type Size = { width: number; height: number };
type LayoutNode = Pick<PlanNode, 'id' | 'sort' | 'status'>;
type LayoutEdge = Pick<PlanEdge, 'source_id' | 'target_id'>;

const bySortThenId = (a: LayoutNode, b: LayoutNode) =>
  a.sort !== b.sort ? a.sort - b.sort : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

/**
 * Longest path from the sources, by Kahn's walk. A cycle nothing feeds
 * releases its earliest (sort, id) node, as plan-tree.js does, and the arrow
 * that closed the cycle is then ignored. Also returns the placement order,
 * a topological order of the graph without those closing arrows.
 */
const assignLayers = (accepted: LayoutNode[], outgoing: Map<string, string[]>, incoming: Map<string, string[]>) => {
  const layer = new Map<string, number>();
  const pending = new Map(accepted.map((n) => [n.id, incoming.get(n.id)!.length]));
  const placed = new Set<string>();
  const order: string[] = [];
  const ready = accepted.filter((n) => pending.get(n.id) === 0).map((n) => n.id);
  accepted.forEach((n) => layer.set(n.id, 0));
  let release = 0;

  while (placed.size < accepted.length) {
    if (ready.length === 0) {
      while (placed.has(accepted[release].id)) release += 1;
      ready.push(accepted[release].id);
    }
    const id = ready.pop()!;
    if (placed.has(id)) continue;
    placed.add(id);
    order.push(id);
    const at = layer.get(id)!;
    outgoing.get(id)!.forEach((child) => {
      if (placed.has(child)) return;
      layer.set(child, Math.max(layer.get(child)!, at + 1));
      const left = pending.get(child)! - 1;
      pending.set(child, left);
      if (left === 0) ready.push(child);
    });
  }
  return { layer, order };
};

/**
 * Pull each card right to the column just before its nearest successor, in
 * reverse topological order so successors have settled first. A one-step lane
 * into a hub then sits beside the hub rather than in column one. A card never
 * passes a predecessor, which sits in an earlier column by construction.
 * Arrows that close a cycle (successor not later) are ignored.
 */
const pullRight = (order: string[], outgoing: Map<string, string[]>, layer: Map<string, number>) => {
  for (let i = order.length - 1; i >= 0; i -= 1) {
    const id = order[i];
    const at = layer.get(id)!;
    const later = outgoing.get(id)!.map((c) => layer.get(c)!).filter((l) => l > at);
    if (later.length === 0) continue;
    const to = Math.min(...later) - 1;
    if (to > at) layer.set(id, to);
  }
};

/**
 * A layered left-to-right layout of the plan's accepted topics, in flow units
 * with the top-left of the whole drawing at (0, 0).
 *
 * Layers by longest path, then pulls cards right toward their successors. An
 * arrow that still spans several columns gets a virtual slot in each column
 * it passes through, so no card sits on its line. Each layer is ordered by the
 * barycenter of its neighbors (two rounds of a down and an up sweep), then
 * columns go 120 apart and cards 40 apart, each column centered on the
 * tallest. Proposals sit in one row under everything. Virtual slots are not
 * returned. O((V + S) log (V + S) + E + S), S the number of virtual slots.
 */
export const tidyLayout = (
  nodes: LayoutNode[],
  edges: LayoutEdge[],
  sizes: Record<string, Size | undefined> | Map<string, Size | undefined>,
): Map<string, XY> => {
  const sizeOf = (id: string): Size => {
    const s = sizes instanceof Map ? sizes.get(id) : sizes[id];
    // A card jsdom (or a hidden pane) measured at zero is as good as unmeasured.
    return s && s.width > 0 && s.height > 0 ? s : FALLBACK_SIZE;
  };

  const accepted = nodes.filter((n) => n.status !== 'proposed').sort(bySortThenId);
  const proposed = nodes.filter((n) => n.status === 'proposed').sort(bySortThenId);
  const rank = new Map(accepted.map((n, i) => [n.id, i]));

  const outgoing = new Map<string, string[]>(accepted.map((n) => [n.id, []]));
  const incoming = new Map<string, string[]>(accepted.map((n) => [n.id, []]));
  const seenEdge = new Set<string>();
  edges.forEach((e) => {
    const key = `${e.source_id}>${e.target_id}`;
    if (e.source_id === e.target_id || seenEdge.has(key) || !rank.has(e.source_id) || !rank.has(e.target_id)) return;
    seenEdge.add(key);
    outgoing.get(e.source_id)!.push(e.target_id);
    incoming.get(e.target_id)!.push(e.source_id);
  });

  const { layer: layerOf, order } = assignLayers(accepted, outgoing, incoming);
  pullRight(order, outgoing, layerOf);

  // The ordering graph: real arrows spanning one column, and long ones split into
  // chains through virtual slots. Slot ids cannot collide with a uuid.
  const ordIn = new Map<string, string[]>(accepted.map((n) => [n.id, []]));
  const ordOut = new Map<string, string[]>(accepted.map((n) => [n.id, []]));
  const virtual = new Set<string>();
  const link = (a: string, b: string) => {
    ordOut.get(a)!.push(b);
    ordIn.get(b)!.push(a);
  };
  accepted.forEach((n) => {
    outgoing.get(n.id)!.forEach((target) => {
      const from = layerOf.get(n.id)!;
      const to = layerOf.get(target)!;
      if (to <= from + 1) {
        link(n.id, target);
        return;
      }
      let prev = n.id;
      for (let l = from + 1; l < to; l += 1) {
        const slot = ` slot ${n.id}>${target}@${l}`;
        virtual.add(slot);
        layerOf.set(slot, l);
        rank.set(slot, rank.size);
        ordIn.set(slot, []);
        ordOut.set(slot, []);
        link(prev, slot);
        prev = slot;
      }
      link(prev, target);
    });
  });

  const layers: string[][] = [];
  const put = (id: string) => {
    const l = layerOf.get(id)!;
    while (layers.length <= l) layers.push([]);
    layers[l].push(id);
  };
  accepted.forEach((n) => put(n.id));
  virtual.forEach(put);

  // Positions as a fraction of the layer's height, so layers of different sizes compare.
  const pos = new Map<string, number>();
  const index = (layer: string[]) => layer.forEach((id, i) => pos.set(id, (i + 0.5) / layer.length));
  layers.forEach(index);

  const mean = (ids: string[]) => ids.reduce((sum, id) => sum + pos.get(id)!, 0) / ids.length;
  const sweep = (l: number, from: number, down: boolean) => {
    const bary = new Map<string, number>();
    layers[l].forEach((id) => {
      const near = down ? ordIn.get(id)! : ordOut.get(id)!;
      const adjacent = near.filter((o) => layerOf.get(o) === from);
      // No neighbor in the adjacent layer (an arrow closing a cycle): pull toward the rest, or stay put.
      const side = near.filter((o) => (down ? layerOf.get(o)! < l : layerOf.get(o)! > l));
      bary.set(id, adjacent.length ? mean(adjacent) : side.length ? mean(side) : pos.get(id)!);
    });
    layers[l].sort((a, b) => bary.get(a)! - bary.get(b)! || rank.get(a)! - rank.get(b)!);
    index(layers[l]);
  };
  for (let round = 0; round < 2; round += 1) {
    for (let l = 1; l < layers.length; l += 1) sweep(l, l - 1, true);
    for (let l = layers.length - 2; l >= 0; l -= 1) sweep(l, l + 1, false);
  }

  const heightOf = (layer: string[]) =>
    layer.reduce((sum, id) => sum + sizeOf(id).height, 0) + ROW_GAP * Math.max(0, layer.length - 1);
  const tallest = layers.reduce((max, layer) => Math.max(max, heightOf(layer)), 0);

  const out = new Map<string, XY>();
  let x = 0;
  layers.forEach((layer) => {
    let y = (tallest - heightOf(layer)) / 2;
    let width = 0;
    layer.forEach((id) => {
      const s = sizeOf(id);
      if (!virtual.has(id)) {
        out.set(id, { x, y });
        width = Math.max(width, s.width);
      }
      y += s.height + ROW_GAP;
    });
    x += (width || FALLBACK_SIZE.width) + COLUMN_GAP;
  });

  let px = 0;
  const py = layers.length ? tallest + PROPOSED_GAP : 0;
  proposed.forEach((n) => {
    out.set(n.id, { x: px, y: py });
    px += sizeOf(n.id).width + ROW_GAP;
  });
  return out;
};
